import { useState, useEffect } from "react";
import { X, AlertTriangle } from "lucide-react";
import type { Lead, LeadTier, CompanySize } from "../types";
import { LEAD_TIERS, TIER_EMOJI, COMPANY_SIZES, SIZE_LABELS, isTierSizeMismatch } from "../constants";
import { useLocale } from "../../../context/LanguageContext";

export interface EditLeadFormData {
  name?: string;
  company?: string;
  size?: CompanySize;
  tier?: LeadTier;
  phone?: string;
  notes?: string;
}

interface EditLeadDialogProps {
  open: boolean;
  lead: Lead;
  onClose: () => void;
  onSubmit: (leadId: string, data: EditLeadFormData) => void;
  loading: boolean;
  error?: string | null;
}

export function EditLeadDialog({
  open,
  lead,
  onClose,
  onSubmit,
  loading,
  error,
}: EditLeadDialogProps) {
  const { t } = useLocale();

  const [name, setName] = useState(lead.name);
  const [company, setCompany] = useState(lead.company);
  const [size, setSize] = useState<CompanySize>(lead.size);
  const [tier, setTier] = useState<LeadTier>(lead.tier);
  const [phone, setPhone] = useState(lead.phone ?? "");
  const [notes, setNotes] = useState(lead.notes ?? "");
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Sync state when a different lead is opened
  useEffect(() => {
    setName(lead.name);
    setCompany(lead.company);
    setSize(lead.size);
    setTier(lead.tier);
    setPhone(lead.phone ?? "");
    setNotes(lead.notes ?? "");
    setTouched({});
  }, [lead._id, lead.name, lead.company, lead.size, lead.tier, lead.phone, lead.notes]);

  if (!open) return null;

  const tierSizeWarning = isTierSizeMismatch(tier, size);

  const hasChanges =
    name !== lead.name ||
    company !== lead.company ||
    size !== lead.size ||
    tier !== lead.tier ||
    phone !== (lead.phone ?? "") ||
    notes !== (lead.notes ?? "");

  const canSubmit =
    name.trim() !== "" &&
    company.trim() !== "" &&
    hasChanges &&
    !loading;

  const handleSubmit = () => {
    if (!name.trim() || !company.trim()) {
      setTouched({ name: true, company: true });
      return;
    }
    if (!hasChanges) return;

    // Only send changed fields
    const data: EditLeadFormData = {};
    if (name !== lead.name) data.name = name.trim();
    if (company !== lead.company) data.company = company.trim();
    if (size !== lead.size) data.size = size;
    if (tier !== lead.tier) data.tier = tier;
    if (phone !== (lead.phone ?? "")) data.phone = phone.trim() || undefined;
    if (notes !== (lead.notes ?? "")) data.notes = notes.trim() || undefined;

    onSubmit(lead._id, data);
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="w-full max-w-lg rounded-lg border border-border bg-card shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-border px-6 py-4">
            <h2 className="text-lg font-semibold text-foreground">
              {t("leads.editLead")}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Body */}
          <div className="space-y-4 px-6 py-5">
            {/* Server error banner */}
            {error && (
              <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-800">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
                <span>{error}</span>
              </div>
            )}

            {/* Email (read-only info) */}
            <div>
              <label className="mb-1 block text-sm font-medium text-muted-foreground">
                {t("leads.form.email")}
              </label>
              <input
                type="email"
                value={lead.email}
                disabled
                className="w-full rounded-md border border-border bg-muted px-3 py-2 text-sm text-muted-foreground"
              />
              <p className="mt-1 text-xs text-muted-foreground">{t("leads.validation.emailReadonly")}</p>
            </div>

            {/* Name */}
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">
                {t("leads.form.name")} *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => setTouched((p) => ({ ...p, name: true }))}
                maxLength={200}
                className={`w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 ${
                  touched.name && !name.trim()
                    ? "border-red-400 focus:border-red-400 focus:ring-red-400"
                    : "border-border focus:border-primary focus:ring-primary"
                }`}
                placeholder={t("leads.form.namePlaceholder")}
              />
              {touched.name && !name.trim() && (
                <p className="mt-1 text-xs text-red-600">{t("leads.validation.required")}</p>
              )}
            </div>

            {/* Company */}
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">
                {t("leads.form.company")} *
              </label>
              <input
                type="text"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                onBlur={() => setTouched((p) => ({ ...p, company: true }))}
                maxLength={200}
                className={`w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 ${
                  touched.company && !company.trim()
                    ? "border-red-400 focus:border-red-400 focus:ring-red-400"
                    : "border-border focus:border-primary focus:ring-primary"
                }`}
                placeholder={t("leads.form.companyPlaceholder")}
              />
              {touched.company && !company.trim() && (
                <p className="mt-1 text-xs text-red-600">{t("leads.validation.required")}</p>
              )}
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {/* Company Size */}
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">
                  {t("leads.form.size")}
                </label>
                <select
                  value={size}
                  onChange={(e) => setSize(e.target.value as CompanySize)}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  {COMPANY_SIZES.map((s) => (
                    <option key={s} value={s}>
                      {SIZE_LABELS[s]} {t("leads.form.employees")}
                    </option>
                  ))}
                </select>
              </div>

              {/* Tier */}
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">
                  {t("leads.form.tier")}
                </label>
                <select
                  value={tier}
                  onChange={(e) => setTier(e.target.value as LeadTier)}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  {LEAD_TIERS.map((tierOpt) => (
                    <option key={tierOpt} value={tierOpt}>
                      {TIER_EMOJI[tierOpt]} {t(`leads.tier.${tierOpt}`)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Tier-size mismatch warning */}
            {tierSizeWarning && (
              <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
                <span>{t("leads.validation.tierSizeMismatch")}</span>
              </div>
            )}

            {/* Phone */}
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">
                {t("leads.form.phone")}
              </label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                maxLength={50}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                placeholder="+49 ..."
              />
            </div>

            {/* Notes */}
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">
                {t("leads.form.notes")}
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                maxLength={2000}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                placeholder={t("leads.form.notesPlaceholder")}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 border-t border-border px-6 py-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-border px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted"
            >
              {t("common.cancel")}
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {loading ? t("common.loading") : t("common.save")}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
